import React, { useEffect, useRef } from "react";
import { connect } from "react-redux";
import {
  Box,
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import GetAppOutlinedIcon from "@material-ui/icons/GetAppOutlined";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import PersonIcon from "@material-ui/icons/Person";
import PieChartIcon from "@material-ui/icons/PieChart";
import LabeledTextField from "../../calc/shared/ui/HaklaiLabeledTextField";
import HelpDialog from "../../../layout/HelpDialog";
import CustomAcordion from "../../../layout/CustomAcordion";
import HaklaiAddressAutocomplete from "./HaklaiAddressAutocomplete";
import useHaklai2990Panel from "./useHaklai2990Panel";
import {
  HAKLAI_2990_ACTIONS,
  HAKLAI_2990_FIELD_LABELS,
  HAKLAI_2990_FIELD_TOOLTIPS,
  HAKLAI_2990_LISHKAT_RISHUM_OPTIONS,
  HAKLAI_2990_LABELS,
  HAKLAI_2990_MESSAGES,
  HAKLAI_2990_SMART_ADDRESS_HELP,
  getHaklai2990ParcelChoiceLabel
} from "./haklai2990Texts";

/**
 * Owns the editable Haklai 2990 result panel.
 *
 * Responsibilities:
 * - renders generated form fields from analysis data
 * - coordinates parcel lookup, save, and document download controls
 * - delegates side effects and payload building to `useHaklai2990Panel`
 */

const SMART_ADDRESS_HELP_DIALOG = (
  <Box textAlign="right">
    <Typography variant="body2" component="div" gutterBottom>
      {HAKLAI_2990_SMART_ADDRESS_HELP.formatsTitle}
    </Typography>
    <ul style={{ margin: "8px 0 0", paddingRight: "18px" }}>
      {HAKLAI_2990_SMART_ADDRESS_HELP.formats.map((format) => (
        <li key={format}>{format}</li>
      ))}
    </ul>
    <Typography variant="body2" component="div" style={{ marginTop: 12 }}>
      {HAKLAI_2990_SMART_ADDRESS_HELP.examplesTitle}
      {HAKLAI_2990_SMART_ADDRESS_HELP.examples.map((example) => (
        <React.Fragment key={example}>
          <br />
          {example}
        </React.Fragment>
      ))}
    </Typography>
  </Box>
);

const SELLER_INPUT_WIDTH = 104;
const OFFICE_INPUT_WIDTH = 128;
const PROPERTY_INPUT_WIDTH = 124;
const FRACTION_INPUT_WIDTH = 124;
const SELLER_FIELDS = [
  { type: "text", label: HAKLAI_2990_FIELD_LABELS.sellerFirstName, name: "sellerFirstName", maxLength: "17", textFieldWidth: SELLER_INPUT_WIDTH },
  { type: "text", label: HAKLAI_2990_FIELD_LABELS.sellerLastName, name: "sellerLastName", maxLength: "17", textFieldWidth: SELLER_INPUT_WIDTH },
  {
    type: "number",
    label: HAKLAI_2990_FIELD_LABELS.sellerId,
    name: "sellerId",
    noComma: true,
    sequencesOfZerosAllowed: true,
    maxLength: "9",
    tooltip: HAKLAI_2990_FIELD_TOOLTIPS.sellerId,
    textFieldWidth: SELLER_INPUT_WIDTH
  },
  {
    type: "select",
    label: HAKLAI_2990_FIELD_LABELS.lishcatRishumLocation,
    name: "lishcatRishumLocation",
    useOfficeOptions: true,
    textFieldWidth: OFFICE_INPUT_WIDTH
  }
];
// One right-to-left sequence. A field wraps only when the current line has no room.
const PROPERTY_FIELDS = [
  { type: "text", label: HAKLAI_2990_FIELD_LABELS.assetCity, name: "assetCity", addressAutocomplete: true, textFieldWidth: PROPERTY_INPUT_WIDTH },
  { type: "text", label: HAKLAI_2990_FIELD_LABELS.assetStreet, name: "assetStreet", addressAutocomplete: true, textFieldWidth: PROPERTY_INPUT_WIDTH },
  { type: "number", label: HAKLAI_2990_FIELD_LABELS.assetStreetNumber, name: "assetStreetNumber", noComma: true, maxLength: "5", textFieldWidth: PROPERTY_INPUT_WIDTH },
  { type: "number", label: HAKLAI_2990_FIELD_LABELS.assetHouseNum, name: "assetHouseNum", noComma: true, maxLength: "4", textFieldWidth: PROPERTY_INPUT_WIDTH },
  { type: "number", label: HAKLAI_2990_FIELD_LABELS.assetZipcode, name: "assetZipcode", noComma: true, maxLength: "7", textFieldWidth: PROPERTY_INPUT_WIDTH },
  { type: "number", label: HAKLAI_2990_FIELD_LABELS.blockNum, name: "blockNum", noComma: true, maxLength: "6", textFieldWidth: PROPERTY_INPUT_WIDTH },
  { type: "number", label: HAKLAI_2990_FIELD_LABELS.subBlock, name: "subBlock", noComma: true, maxLength: "4", textFieldWidth: PROPERTY_INPUT_WIDTH },
  { type: "number", label: HAKLAI_2990_FIELD_LABELS.subSubBlock, name: "subSubBlock", noComma: true, maxLength: "3", textFieldWidth: PROPERTY_INPUT_WIDTH }
];
const FRACTION_DENOMINATOR_FIELD = {
  type: "number",
  label: HAKLAI_2990_LABELS.denominator,
  name: "passPartMekarkeinMechane",
  noComma: true,
  maxLength: "12",
  textFieldWidth: FRACTION_INPUT_WIDTH
};
const FRACTION_NUMERATOR_FIELD = {
  type: "number",
  label: HAKLAI_2990_LABELS.numerator,
  name: "passPartMekarkeinMone",
  noComma: true,
  maxLength: "12",
  textFieldWidth: FRACTION_INPUT_WIDTH
};
const PARCEL_SEARCH_INPUT_ID = "haklai-2990-parcelAddressSearch";
const getFieldId = (fieldName) => `haklai-2990-${fieldName}`;

const useStyles = makeStyles((theme) => ({
  lookupInfoBox: {
    color: "#1f7a4d",
    display: "block",
    fontWeight: 600,
    lineHeight: 1.45
  },
  lookupError: {
    color: theme.palette.error.main,
    fontWeight: 600
  },
  jsonErrorField: {
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: `${theme.palette.error.main} !important`
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: `${theme.palette.error.main} !important`
    },
    "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: `${theme.palette.error.main} !important`
    },
    "& .MuiFormHelperText-root": {
      color: theme.palette.error.main
    }
  },
  jsonEmptyState: {
    color: "#7a8fa3",
    marginTop: theme.spacing(1.25)
  }
}));

const HaklaiResults2990Panel = ({
  dealId,
  haklaiDraftPreCalc,
  analyzedPreCalc,
  fullName,
  licenseNumber,
  disableTaxAuthorityDocumentConfirmation
}) => {
  const classes = useStyles();
  const {
    activeJsonButton,
    addressChoices,
    addresses,
    addressMenuAnchor,
    addressSearchLoading,
    closeAddressParcelMenu,
    docFields,
    exportGroups,
    exportGroupsError,
    handleAddressChoiceClick,
    handleAddressKeyPress,
    handleAddressSearchClick,
    handleDocFieldChange,
    handleGenerateDeductions,
    handleGenerateJson,
    handleGeneratePdf,
    handleParcelSearchFieldChange,
    handleSave,
    hasGovMapAutofill,
    isDocumentActionBusy,
    isGeneratingDeductions,
    isGeneratingPdf,
    isLoading,
    isSaving,
    jsonFieldErrors,
    jsonValidationFocusRequest,
    loadError,
    parcelAddressError,
    parcelAddressSearch,
    parcelMenuAnchorRef,
    parcelPairError
  } = useHaklai2990Panel({
    analyzedPreCalc,
    dealId,
    haklaiDraftPreCalc,
    taxAuthorityAccountIdentity: {
      fullName,
      licenseNumber,
      disableTaxAuthorityDocumentConfirmation
    }
  });

  const fieldRefs = useRef({});
  const registerFieldRef = (fieldName) => (node) => {
    if (node) fieldRefs.current[fieldName] = node;
    else delete fieldRefs.current[fieldName];
  };

  useEffect(() => {
    const fieldName = jsonValidationFocusRequest?.fieldName;
    if (!fieldName) return;

    const fieldElement = fieldRefs.current[fieldName];
    if (!fieldElement) return;

    if (typeof fieldElement.scrollIntoView === "function") {
      fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    const focusTarget = fieldElement.querySelector("input, textarea, button, [tabindex]");
    if (focusTarget && typeof focusTarget.focus === "function") {
      try {
        focusTarget.focus({ preventScroll: true });
      } catch {
        focusTarget.focus();
      }
    }
  }, [jsonValidationFocusRequest]);

  const officeOptions = HAKLAI_2990_LISHKAT_RISHUM_OPTIONS;
  const fieldsDisabled = isLoading || isDocumentActionBusy;
  const renderFieldControl = (field, fieldId, { hideHelper = false, className = "" } = {}) => {
    const {
      label,
      tooltip,
      useOfficeOptions,
      addressAutocomplete,
      ...textFieldProps
    } = field;
    const inputNode = addressAutocomplete ? (
      <HaklaiAddressAutocomplete
        addresses={addresses}
        city={docFields.assetCity}
        disabled={fieldsDisabled || textFieldProps.disabled}
        error={Boolean(jsonFieldErrors[field.name]) || textFieldProps.error}
        helperText={hideHelper ? undefined : (jsonFieldErrors[field.name] || textFieldProps.helperText)}
        id={fieldId}
        label={label}
        name={field.name}
        onChange={handleDocFieldChange}
        textFieldWidth={field.textFieldWidth}
        value={docFields[field.name]}
      />
    ) : (
      <LabeledTextField
        {...textFieldProps}
        id={fieldId}
        selectLabelId={field.type === "select" ? `${fieldId}-label` : undefined}
        options={useOfficeOptions ? officeOptions : field.options}
        value={docFields[field.name]}
        textFieldWidth={field.textFieldWidth}
        labelSpacing={0}
        inputSpacing={0}
        disabled={fieldsDisabled || textFieldProps.disabled}
        error={Boolean(jsonFieldErrors[field.name]) || textFieldProps.error}
        helperText={hideHelper ? undefined : (jsonFieldErrors[field.name] || textFieldProps.helperText || undefined)}
        onChange={handleDocFieldChange}
      />
    );

    return (
      <div
        className={["haklai-2990-pair-control", className, jsonFieldErrors[field.name] ? classes.jsonErrorField : ""]
          .filter(Boolean)
          .join(" ")}
        ref={registerFieldRef(field.name)}
      >
        {tooltip ? (
          <Tooltip title={tooltip} placement="bottom-start">
            <div>{inputNode}</div>
          </Tooltip>
        ) : inputNode}
      </div>
    );
  };
  const fractionErrors = [
    jsonFieldErrors[FRACTION_NUMERATOR_FIELD.name],
    jsonFieldErrors[FRACTION_DENOMINATOR_FIELD.name]
  ].filter(Boolean);
  const renderFieldPair = (field) => {
    const fieldId = getFieldId(field.name);
    return (
      <div className="haklai-2990-pair" key={field.name}>
        <label className="haklai-2990-pair-label" htmlFor={fieldId} id={`${fieldId}-label`}>
          {field.label}
        </label>
        {renderFieldControl(field, fieldId)}
      </div>
    );
  };

  return (
    <>
      <CustomAcordion summaryText={HAKLAI_2990_LABELS.panelTitle} defaultExpanded={true}>
        <div className="haklai-2990-form">
          <div className="haklai-2990-details">
            <section className="haklai-2990-seller">
              <h3 className="haklai-2990-section-title">
                <PersonIcon className="haklai-2990-section-icon" />
                {HAKLAI_2990_LABELS.sellerDetails}
              </h3>
              <div className="haklai-2990-seller-fields">
                {SELLER_FIELDS.map(renderFieldPair)}
              </div>
            </section>

            <section className="haklai-2990-identity">
              <h3 className="haklai-2990-section-title">
                <LocationOnIcon className="haklai-2990-section-icon" />
                {HAKLAI_2990_LABELS.propertyIdentification}
              </h3>
              <div className="haklai-2990-lookup" ref={parcelMenuAnchorRef}>
                <div className="haklai-2990-lookup-label">
                  <label htmlFor={PARCEL_SEARCH_INPUT_ID}>{HAKLAI_2990_LABELS.propertyLookup}</label>
                  <HelpDialog
                    label={HAKLAI_2990_LABELS.propertyLookup}
                    helpDialog={SMART_ADDRESS_HELP_DIALOG}
                  />
                </div>
                <div className="haklai-2990-lookup-field">
                  <TextField
                    id={PARCEL_SEARCH_INPUT_ID}
                    className="haklai-2990-lookup-input"
                    disabled={fieldsDisabled}
                    name="parcelAddressSearch"
                    onChange={handleParcelSearchFieldChange}
                    onKeyPress={handleAddressKeyPress}
                    placeholder={HAKLAI_2990_SMART_ADDRESS_HELP.placeholder}
                    size="small"
                    value={parcelAddressSearch}
                    variant="outlined"
                  />
                  <div className="haklai-2990-lookup-status" aria-live="polite">
                    {parcelAddressError ? (
                      <span className={classes.lookupError}>{parcelAddressError}</span>
                    ) : hasGovMapAutofill ? (
                      <span className={classes.lookupInfoBox}>{HAKLAI_2990_MESSAGES.smartAddressAutofill}</span>
                    ) : null}
                  </div>
                </div>
                <Button
                  className="haklai-2990-search"
                  disabled={fieldsDisabled || addressSearchLoading || !String(parcelAddressSearch || "").trim()}
                  onClick={handleAddressSearchClick}
                  endIcon={addressSearchLoading ? <CircularProgress color="inherit" size={16} /> : null}
                >
                  {HAKLAI_2990_ACTIONS.searchPropertyDetails}
                </Button>
              </div>
              {loadError ? (
                <Typography variant="body2" className={`haklai-2990-message ${classes.lookupError}`}>
                  {loadError}
                </Typography>
              ) : null}
              <Menu
                anchorEl={addressMenuAnchor}
                open={Boolean(addressMenuAnchor) && addressChoices.length > 1}
                onClose={closeAddressParcelMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                getContentAnchorEl={null}
              >
                {addressChoices.map((pair) => (
                  <MenuItem
                    key={`${pair.gush}-${pair.helka}-${pair.objectId != null ? pair.objectId : ""}`}
                    onClick={() => handleAddressChoiceClick(pair)}
                  >
                    {getHaklai2990ParcelChoiceLabel(pair)}
                  </MenuItem>
                ))}
              </Menu>

              <div className="haklai-2990-property-grid">
                {PROPERTY_FIELDS.map(renderFieldPair)}
              </div>
              {parcelPairError ? (
                <Typography variant="body2" className={`haklai-2990-message ${classes.lookupError}`}>
                  {parcelPairError}
                </Typography>
              ) : null}

              <div className="haklai-2990-fraction">
                <div className="haklai-2990-fraction-caption">
                  <PieChartIcon className="haklai-2990-section-icon" />
                  {HAKLAI_2990_LABELS.propertyShare}
                </div>
                <label
                  className="haklai-2990-fraction-term"
                  htmlFor={getFieldId(FRACTION_NUMERATOR_FIELD.name)}
                  id={`${getFieldId(FRACTION_NUMERATOR_FIELD.name)}-label`}
                >
                  {FRACTION_NUMERATOR_FIELD.label}
                </label>
                {renderFieldControl(
                  FRACTION_NUMERATOR_FIELD,
                  getFieldId(FRACTION_NUMERATOR_FIELD.name),
                  { hideHelper: true, className: "haklai-2990-fraction-numerator" }
                )}
                <span className="haklai-2990-fraction-bar" aria-hidden="true" />
                <label
                  className="haklai-2990-fraction-term"
                  htmlFor={getFieldId(FRACTION_DENOMINATOR_FIELD.name)}
                  id={`${getFieldId(FRACTION_DENOMINATOR_FIELD.name)}-label`}
                >
                  {FRACTION_DENOMINATOR_FIELD.label}
                </label>
                {renderFieldControl(
                  FRACTION_DENOMINATOR_FIELD,
                  getFieldId(FRACTION_DENOMINATOR_FIELD.name),
                  { hideHelper: true, className: "haklai-2990-fraction-denominator" }
                )}
                <div className={`haklai-2990-fraction-message ${classes.lookupError}`} aria-live="polite">
                  {fractionErrors.map((message) => (
                    <span key={message}>{message}</span>
                  ))}
                </div>
              </div>
            </section>
          </div>

          <div className="haklai-2990-actions">
            <Button
              className={`haklai-2990-save${isSaving ? " is-saving" : ""}`}
              disabled={fieldsDisabled}
              onClick={handleSave}
            >
              <span className="haklai-2990-save-label">
                {HAKLAI_2990_ACTIONS.savePersonalDetails}
              </span>
              {isSaving ? (
                <CircularProgress className="haklai-2990-save-progress" color="inherit" size={16} />
              ) : null}
            </Button>
          </div>
        </div>
      </CustomAcordion>

      <CustomAcordion summaryText={HAKLAI_2990_LABELS.documentsTitle} defaultExpanded={true}>
        <div className="deal-sum-docs">
          <div className="deal-sum-docs-block">
            <Typography variant="subtitle1" align="right">
              {HAKLAI_2990_LABELS.additionalActions}
            </Typography>
            <div className="deal-sum-action-buttons">
              <Button
                className="deal-sum-admin-btn"
                endIcon={isGeneratingPdf ? <CircularProgress color="inherit" size={18} /> : <DescriptionOutlinedIcon />}
                disabled={fieldsDisabled || Boolean(exportGroupsError)}
                onClick={handleGeneratePdf}
              >
                {HAKLAI_2990_ACTIONS.generateForm2990}
              </Button>
              <Button
                className="deal-sum-admin-btn"
                endIcon={isGeneratingDeductions ? <CircularProgress color="inherit" size={18} /> : <DescriptionOutlinedIcon />}
                disabled={fieldsDisabled || Boolean(exportGroupsError)}
                onClick={handleGenerateDeductions}
              >
                {HAKLAI_2990_ACTIONS.generateDeductions}
              </Button>
            </div>
          </div>

          <div className="deal-sum-docs-block">
            <Typography variant="subtitle1" align="right">
              {HAKLAI_2990_LABELS.onlineFiles}
            </Typography>

            {exportGroupsError || exportGroups.length === 0 ? (
              <Typography variant="body2" className={classes.jsonEmptyState}>
                {exportGroupsError || HAKLAI_2990_MESSAGES.exportFilesEmpty}
              </Typography>
            ) : null}

            <div className="deal-sum-file-buttons">
              {exportGroups.map((group) => {
                const groupIdentity = group.haklaiPropertyKey;
                return (
                  <Box key={`json-group-${groupIdentity}`} className="deal-sum-file-group">
                    <div className="deal-sum-file-group-title">
                      {group.title}
                    </div>
                    <div className="deal-sum-file-buttons">
                      {group.needsMutav ? (
                        <Button
                          className="deal-sum-primary-btn"
                          endIcon={
                            activeJsonButton === `mutav-${groupIdentity}`
                              ? <CircularProgress color="inherit" size={16} />
                              : <GetAppOutlinedIcon />
                          }
                          disabled={fieldsDisabled}
                          onClick={() => handleGenerateJson("mutav", groupIdentity)}
                        >
                          {HAKLAI_2990_ACTIONS.generateMutavFile}
                        </Button>
                      ) : null}
                      {group.needsRegTax ? (
                        <Button
                          className="deal-sum-primary-btn"
                          endIcon={
                            activeJsonButton === `linear-${groupIdentity}`
                              ? <CircularProgress color="inherit" size={16} />
                              : <GetAppOutlinedIcon />
                          }
                          disabled={fieldsDisabled}
                          onClick={() => handleGenerateJson("linear", groupIdentity)}
                        >
                          {HAKLAI_2990_ACTIONS.generateLinearFile}
                        </Button>
                      ) : null}
                      {!group.needsMutav && !group.needsRegTax ? (
                        <Typography variant="body2" className={classes.jsonEmptyState}>
                          {HAKLAI_2990_MESSAGES.noExportRoutesForPart}
                        </Typography>
                      ) : null}
                    </div>
                  </Box>
                );
              })}
            </div>
          </div>
        </div>
      </CustomAcordion>
    </>
  );
};

const mapStateToProps = (state) => ({
  fullName: state.loggedinReducer.fullName,
  licenseNumber: state.loggedinReducer.licenseNumber,
  disableTaxAuthorityDocumentConfirmation: state.loggedinReducer.disableTaxAuthorityDocumentConfirmation
});

export default connect(mapStateToProps)(React.memo(HaklaiResults2990Panel));
